import { makeStyles } from '@material-ui/core/styles';
import { reskinStyles } from 'core/components/theme/theme-variant';

import { SIDE_COLUMN_PULL_UP } from 'core/components/layout-with-sidebar/layout-with-sidebar.styles';

const styles = (theme) => ({
  timelineTitle: {
    color: theme.colorPalette.green_90,
    fontSize: 16,
    margin: 0,
    fontWeight: 'normal',
    textShadow: '0 2px 4px 0 rgba(0,0,0,0.14)'
  },
  sectionHeader: {
    marginBottom: 22
  },
  timelineSubtitle: {
    color: theme.colorPalette.green_100,
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'uppercase',
    marginBottom: 8,
    marginTop: 3
  },
  versionsList: {
    padding: 0,
    margin: 0,
    listStyle: 'none'
  },
  versionsListItem: {
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    paddingLeft: 8
  },
  vListItemRow: {
    display: 'flex',
    flexDirection: 'row'
  },
  vListItemCards: {
    marginBottom: 20,
    flexGrow: 1
  },
  vListItemContent: {
    flex: '1 1',
    display: 'flex',
    flexDirection: 'column'
  },
  valueField: {
    marginBottom: 0
  },
  valueFieldInline: {
    marginBottom: 0,
    display: 'inline-block'
  },
  icon: {
    height: 24,
    width: 24,
    margin: '0px 8px',
    verticalAlign: 'bottom'
  },
  timesIcon: {
    composes: '$icon'
  },
  equalsIcon: {
    composes: '$icon'
  },
  valueFieldContent: {
    marginTop: 0
  },
  dot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    border: `2px solid ${theme.colorPalette.orange_50}`,
    background: theme.colorPalette.beige_50,
    borderRadius: '50%'
  },
  selectedDot: {
    background: theme.colorPalette.orange_50
  },
  timelineDot: {
    left: 0,
    position: 'absolute',
    zIndex: 5
  },
  segmentDot: ({ isInPolicyPreviewMode, active, isLocked }) => ({
    // The active (selected) segment bullet is filled orange; others stay beige.
    // Set here (a dynamic rule) rather than only on activeDot, because this
    // function style is injected after the static activeDot rule and would
    // otherwise override its orange fill back to beige.
    background: active ? theme.colorPalette.orange_50 : theme.colorPalette.beige_50,
    width: 20,
    height: 20,
    left: -1,
    cursor: isLocked || isInPolicyPreviewMode ? 'default' : 'pointer',
    padding: 0,
    // Locked segments are non-interactive: no hover ring animation.
    '&:hover': isLocked
      ? {}
      : {
          boxShadow: `0px 0px 0px 10px ${theme.colorPalette.orange_50_op_08}`,
          background: active ? theme.colorPalette.orange_50 : theme.colorPalette.beige_50
        }
  }),
  segmentsListItem: ({ isInPolicyPreviewMode, isDraft, isMutual }) => ({
    paddingLeft: 0,
    // Mutual keeps the whole item readable (the date must stay legible) and dims only
    // the card body via lockedContent; Branch keeps the legacy whole-item dim.
    opacity: !isMutual && isInPolicyPreviewMode && !isDraft ? 0.6 : 1
  }),
  plusDot: {
    position: 'absolute',
    top: 7,
    left: -11
  },
  vItemCard: {
    padding: 24,
    boxShadow: '0 0 2px 0 rgba(0,0,0,0.12), 0 2px 2px 0 rgba(0,0,0,0.24)',
    marginBottom: 7,
    // Reskin: cards are outlined, never shadowed.
    ...reskinStyles(theme, { boxShadow: 'none' })
  },
  vItemCardDraft: {
    padding: 0,
    overflow: 'hidden'
  },
  draftHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 16px',
    backgroundColor: theme.colorPalette.beige_60
  },
  draftHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  draftHeaderText: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.colorPalette.green_90
  },
  draftInfoIcon: {
    width: 16,
    height: 16,
    color: theme.colorPalette.green_90
  },
  draftHelpIcon: {
    width: 12,
    height: 12,
    color: theme.colors.action_text,
    marginLeft: theme.spacing(2)
  },
  draftCardContent: {
    padding: 24
  },
  vItemSegment: {
    marginBottom: 24
  },
  segmentsList: {
    paddingLeft: 0,
    margin: 0,
    listStyle: 'none'
  },
  segmentsContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  },
  segmentsBottomWrapper: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    marginRight: 24
  },
  segmentsBottomLine: {
    display: 'inline-block',
    width: 11,
    height: 16
  },
  segmentsBottomTracker: {
    display: 'inline-block',
    height: '100%',
    borderLeft: `2px solid ${theme.colorPalette.orange_50}`
  },
  segmentTimelineTracker: ({ isDraft, isInPolicyPreviewMode }) => ({
    position: 'relative',
    width: 11,
    marginRight: 24,
    borderRight: `2px ${isDraft && isInPolicyPreviewMode ? 'dashed' : 'solid'} ${theme.colorPalette.orange_50}`,
    display: ' inline-block'
  }),
  segmentContent: ({ isInPolicyPreviewMode }) => ({
    width: '100%',
    cursor: isInPolicyPreviewMode ? 'default' : 'pointer'
  }),
  button: {
    width: 288,
    float: 'right'
  },
  activeDot: {
    background: theme.colorPalette.orange_50,
    boxShadow: '0 8px 8px 0 rgba(0, 0, 0, 0.24), 0 0 8px 0 rgba(0, 0, 0, 0.12)',
    '&:hover': {
      boxShadow: '0 8px 8px 0 rgba(0, 0, 0, 0.24), 0 0 8px 0 rgba(0, 0, 0, 0.12)',
      background: theme.colorPalette.orange_50
    }
  },
  activeCard: {
    boxShadow: '0 8px 8px 0 rgba(0, 0, 0, 0.24), 0 0 8px 0 rgba(0, 0, 0, 0.12)',
    // Reskin: the active version gets a darker outline instead of a shadow.
    ...reskinStyles(theme, { boxShadow: 'none', borderColor: theme.colorPalette.beige_60 })
  },
  innerActive: {
    display: 'inline-block'
  },
  firstContainer: {
    marginBottom: 26
  },
  // Expanded sections space themselves; a collapsed one needs the same Section spacing
  collapsedSortableItem: {
    marginBottom: theme.spacing(2)
  },
  // The side column is pulled up into the title's whitespace; the toggle sits below the title instead
  layoutToggle: {
    marginTop: SIDE_COLUMN_PULL_UP
  },
  changed: {
    marginBottom: theme.sizes.footerHeight + 50
  },
  clarionDoorLink: {
    color: theme.palette.secondary.main,
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  mutualRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: theme.spacing(2)
  },
  mutualRowLabel: {
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    color: theme.colorPalette.green_75
  },
  mutualRowValue: {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    textAlign: 'right',
    color: theme.colorPalette.green_80
  },
  // Only the Policy Premium value is emphasized (SemiBold) per Figma Body2_Semibold;
  // Created by / Created values stay regular (400).
  mutualRowValueEmphasis: {
    fontWeight: 600
  },
  mutualDivider: {
    border: 'none',
    borderTop: `1px solid ${theme.colorPalette.beige_50_op_50}`,
    // 16px of space above and below the divider line.
    margin: theme.spacing(4, 0)
  },
  timelineSubtitleMutual: {
    color: theme.colorPalette.green_100,
    fontSize: 14,
    fontWeight: 700,
    textTransform: 'none',
    marginBottom: 8,
    marginTop: 3,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  lockIcon: {
    width: 16,
    height: 16
  },
  lockedContent: {
    opacity: 0.5
  },
  // Dim the timeline dot + connector line for locked segments so they don't
  // look clickable (the date + lock icon stay at full opacity).
  lockedTimeline: {
    opacity: 0.5
  }
});

export default makeStyles(styles);
